import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, IconButton, Menu, Select, Skeleton, TextField, useConfirm, useToast } from '../ui';
import {
  ArrowDownIcon, ArrowUpIcon, ChevronRightIcon, EditIcon, LocationIcon, MoreIcon, PlusIcon, TrashIcon,
} from '../ui/icons';
import api from '../config/api';
import { invalidateSavedViews } from '../shell/savedViews';
import { locationErrorMessage, pathLabel, placeName, useLocations } from '../locations';

/**
 * The places of one location type as a tree: expand and collapse, item
 * counts, and for its owner add inside, rename inline, move up and down
 * among siblings, move to another parent, delete when empty (each place's
 * "…" menu). The Unsorted root holds places found in items that were not in
 * the list.
 */
function LocationTree({ locationType, canManage, onChanged }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const { places, loading, error, reload } = useLocations(locationType.id);
  const [expanded, setExpanded] = useState(() => new Set());
  const [renaming, setRenaming] = useState(null);     // {id, name}
  const [adding, setAdding] = useState(null);         // {parentId, name}
  const [moving, setMoving] = useState(null);         // id
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const children = useMemo(() => {
    const map = new Map();
    places.forEach(place => {
      const key = place.parent_id ?? 0;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(place);
    });
    return map;
  }, [places]);

  const run = async (request) => {
    setBusy(true);
    setMessage(null);
    try {
      const response = await request();
      // A place gone or moved changes what saved views keep: read them again.
      invalidateSavedViews();
      await reload();
      if (onChanged) onChanged();
      return response;
    } catch (err) {
      setMessage(locationErrorMessage(t, err));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submitAdd = async () => {
    const name = adding.name.trim();
    if (!name) return;
    const done = await run(() => api.post(`/location-types/${locationType.id}/locations`, { name, parent_id: adding.parentId }));
    if (done) {
      if (adding.parentId) setExpanded(prev => new Set(prev).add(adding.parentId));
      setAdding(null);
    }
  };

  const submitRename = async () => {
    const name = renaming.name.trim();
    if (!name) return;
    if (await run(() => api.put(`/locations/${renaming.id}`, { name }))) setRenaming(null);
  };

  const moveBy = (place, delta) => {
    const siblings = children.get(place.parent_id ?? 0) || [];
    const index = siblings.findIndex(p => p.id === place.id);
    return run(() => api.put(`/locations/${place.id}`, { position: index + delta }));
  };

  const moveTo = async (place, parentValue) => {
    const parentId = parentValue === '' ? null : Number(parentValue);
    if (await run(() => api.put(`/locations/${place.id}`, { parent_id: parentId }))) {
      setMoving(null);
      if (parentId) setExpanded(prev => new Set(prev).add(parentId));
    }
  };

  const remove = async (place, kids) => {
    // Only an empty place goes: say why the others stay.
    if (kids.length > 0 || place.item_count > 0) {
      toast.info(t('locations.cannotDelete', { places: kids.length, items: place.item_count }));
      return;
    }
    if (await confirm({
      title: t('locations.confirmDeleteTitle'),
      message: t('locations.confirmDelete', { name: placeName(t, place) }),
      confirmLabel: t('locations.deletePlace'),
      danger: true,
    })) {
      run(() => api.delete(`/locations/${place.id}`));
    }
  };

  /** Places a place may move into: anything outside its own subtree. */
  const targetsFor = (place) => {
    const inside = new Set([place.id]);
    places.forEach(p => { if (p.parent_id !== null && inside.has(p.parent_id)) inside.add(p.id); });
    return places.filter(p => !inside.has(p.id));
  };

  const indent = (depth) => ({ '--depth': depth });

  const addForm = (parentId, depth) => (
    <div className="place-row__form" style={indent(depth)}>
      <TextField
        label={parentId ? t('locations.addInside', { path: pathLabel(t, places.find(p => p.id === parentId)?.path) }) : t('locations.addTopLevel')}
        hideLabel
        // A form opened on purpose takes the focus.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        maxLength={255}
        value={adding.name}
        onChange={(e) => setAdding({ ...adding, name: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); submitAdd(); }
          if (e.key === 'Escape') { e.preventDefault(); setAdding(null); }
        }}
        placeholder={t('locations.newPlace')}
      />
      <Button size="sm" onClick={submitAdd} disabled={busy || !adding.name.trim()}>{t('locations.add')}</Button>
      <Button size="sm" variant="ghost" onClick={() => setAdding(null)}>{t('common.cancel')}</Button>
    </div>
  );

  const renderNode = (place, index, siblings) => {
    const kids = children.get(place.id) || [];
    const open = expanded.has(place.id);
    const name = placeName(t, place);
    const isRenaming = renaming && renaming.id === place.id;
    const menu = [
      { id: 'add', label: t('locations.addInsideTitle', { name }), icon: PlusIcon, onSelect: () => setAdding({ parentId: place.id, name: '' }) },
      ...(!place.is_unsorted ? [
        { id: 'rename', label: t('locations.rename'), icon: EditIcon, onSelect: () => setRenaming({ id: place.id, name: place.name }) },
        { id: 'up', label: t('locations.moveUp', { name }), icon: ArrowUpIcon, disabled: index === 0, onSelect: () => moveBy(place, -1) },
        { id: 'down', label: t('locations.moveDown', { name }), icon: ArrowDownIcon,
          disabled: index === siblings.length - 1 || !!siblings[index + 1]?.is_unsorted, onSelect: () => moveBy(place, 1) },
        { id: 'move', label: t('locations.moveTo'), icon: LocationIcon, onSelect: () => setMoving(place.id) },
      ] : []),
      'separator',
      { id: 'delete', label: t('common.delete'), icon: TrashIcon, danger: true, onSelect: () => remove(place, kids) },
    ];
    return (
      <li key={place.id}>
        <div className={`place-row${place.is_unsorted ? ' is-unsorted' : ''}`} style={indent(place.depth)}>
          {kids.length > 0 ? (
            <IconButton icon={ChevronRightIcon} variant="ghost" size="sm" className={`place-row__toggle${open ? ' is-open' : ''}`}
              onClick={() => toggle(place.id)} aria-expanded={open}
              label={open ? t('locations.collapse', { name }) : t('locations.expand', { name })} />
          ) : <span className="place-row__toggle place-row__toggle--leaf" aria-hidden="true" />}

          {isRenaming ? (
            <div className="place-row__rename">
              <TextField
                label={t('locations.renameNamed', { name })}
                hideLabel
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                maxLength={255}
                value={renaming.name}
                onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); submitRename(); }
                  if (e.key === 'Escape') { e.preventDefault(); setRenaming(null); }
                }}
              />
              <Button size="sm" onClick={submitRename} disabled={busy || !renaming.name.trim()}>{t('common.save')}</Button>
              <Button size="sm" variant="ghost" onClick={() => setRenaming(null)}>{t('common.cancel')}</Button>
            </div>
          ) : (
            <span className="place-row__name">
              <span className="place-row__label">{name}</span>
              {kids.length > 0 && !open && <span className="place-row__meta">{t('locations.placeCount', { count: kids.length })}</span>}
              {place.item_count > 0 && <span className="place-row__meta">{t('locations.itemCount', { count: place.item_count })}</span>}
            </span>
          )}

          {canManage && !isRenaming && (
            <span className="place-row__actions">
              <IconButton icon={PlusIcon} variant="ghost" size="sm" disabled={busy} className="place-row__add"
                label={t('locations.addInsideTitle', { name })} onClick={() => setAdding({ parentId: place.id, name: '' })} />
              <Menu
                label={t('locations.actionsFor', { name })}
                align="end"
                items={menu}
                trigger={(props) => (
                  <IconButton {...props} icon={MoreIcon} variant="ghost" size="sm" disabled={busy} label={t('locations.actionsFor', { name })} />
                )}
              />
            </span>
          )}
        </div>

        {place.is_unsorted && (
          <p className="place-row__hint" style={indent(place.depth)}>{t('locations.unsortedHint')}</p>
        )}

        {moving === place.id && (
          <div className="place-row__form" style={indent(place.depth + 1)}>
            <Select id={`move-${place.id}`} label={t('locations.moveInto', { name })} defaultValue={place.parent_id ?? ''} disabled={busy}
              // "Move to…" was chosen in the menu: the choice of a parent comes next.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              onChange={(e) => moveTo(place, e.target.value)}>
              <option value="">{t('locations.topLevel')}</option>
              {targetsFor(place).map(target => (
                <option key={target.id} value={target.id}>{pathLabel(t, target.path)}</option>
              ))}
            </Select>
            <Button size="sm" variant="ghost" onClick={() => setMoving(null)}>{t('common.cancel')}</Button>
          </div>
        )}

        {adding && adding.parentId === place.id && addForm(place.id, place.depth + 1)}

        {open && kids.length > 0 && (
          <ul className="place-tree">
            {kids.map((kid, i) => renderNode(kid, i, kids))}
          </ul>
        )}
      </li>
    );
  };

  const roots = children.get(0) || [];
  return (
    <div className="place-tree-wrap">
      {message && <Alert tone="danger">{message}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}
      {loading && places.length === 0 ? (
        <Skeleton height={120} />
      ) : roots.length === 0 && !adding ? (
        <p className="settings-muted">{t('locations.empty')}</p>
      ) : (
        <ul aria-label={t('locations.treeLabel', { name: locationType.name })} className="place-tree place-tree--root">
          {roots.map((root, i) => renderNode(root, i, roots))}
        </ul>
      )}
      {canManage && (
        adding && adding.parentId === null
          ? addForm(null, 0)
          : (
            <Button variant="secondary" size="sm" icon={PlusIcon} disabled={busy} className="place-tree__add-root"
              onClick={() => setAdding({ parentId: null, name: '' })}>
              {t('locations.addRoot')}
            </Button>
          )
      )}
    </div>
  );
}

export default LocationTree;
